import { useRouter } from '@belong/common';
import { IconFb, IconLinkedin, IconInstagram } from '@belong/icons';
import { Image, Link, LinkBase, Text } from '@belong/ui';
import clsx from 'clsx';
import { ConsentPreferencesLink } from 'src/features/consent/components/consent-preferences-link/consent-preferences-link';
import css from './footer.module.css';
import {
  WHO_WE_ARE_COLUMN_LINKS,
  SUPPORT_COLUMN_LINKS,
  BELONG_PRO_LINKS,
} from './footer.constants';

type FooterLinkProps = {
  pathname: string;
  label: string;
  shallow?: boolean;
  search?: string;
  to?: string;
  /** Small green pill after the label, for a link worth pointing at. */
  badge?: string;
};

function FooterLink({
  pathname,
  to,
  search,
  shallow,
  label,
  badge,
}: FooterLinkProps) {
  const href = to || { pathname, search };

  return (
    <Link href={href} shallow={shallow}>
      <span className="flex items-center gap-2xs leading-none">
        <Text variant="body" fontWeight="semibold" className="lg:p1">
          {label}
        </Text>
        {badge ? (
          <Text
            as="span"
            variant="p2"
            fontWeight="semibold"
            // `leading-none` and a fixed height, so the pill centres on the
            // label's optical middle: the p2 line-height was padding the text
            // unevenly inside it and pushing the badge off the line.
            className="flex h-[18px] items-center rounded-small bg-green px-2xs uppercase leading-none text-white"
          >
            {badge}
          </Text>
        ) : null}
      </span>
    </Link>
  );
}

export function Footer(): JSX.Element {
  const { pathname, asPath } = useRouter();

  let routerPathname = pathname;
  // Using asPath in Next because it can be a dynamic route
  if (asPath) {
    routerPathname = new URL(asPath, 'http://n').pathname;
  }

  return (
    <footer className="container mb-3xl py-2xl ">
      <div
        className={clsx(
          'mb-xl grid gap-3xl lg:gap-2xl xl:gap-4xl',
          css.footerTopGrid,
        )}
      >
        <div>
          <div className="mb-xl flex shrink-0 items-center md:mb-lg">
            {/* Accecibility, alt tags? */}
            <div className="mr-xs w-[33px] shrink-0">
              <Image src="/logos/belong_logo_symbol.svg" alt="" />
            </div>
            <div>
              <Text className="whitespace-nowrap">Powered by AI.</Text>
              <Text className="whitespace-nowrap">
                Made and Loved by People.
              </Text>
            </div>
          </div>
          <div className="flex items-center">
            <LinkBase
              href="https://www.facebook.com/belonghome/"
              target="_blank"
              aria-label="Link To Belong Facebook"
            >
              <IconFb
                height={24}
                className="mr-xl hover:text-dark-navy md:mr-sm"
                aria-hidden="true"
              />
            </LinkBase>
            <LinkBase
              href="https://www.linkedin.com/company/belonghq/mycompany/"
              target="_blank"
              aria-label="Link To Belong Linkedin"
            >
              <IconLinkedin
                height={24}
                className="mr-xl hover:text-dark-navy md:mr-sm"
                aria-hidden="true"
              />
            </LinkBase>
            <LinkBase
              href="https://www.instagram.com/belonghm"
              target="_blank"
              aria-label="Link To Belong Instagram"
            >
              <IconInstagram
                height={24}
                className="hover:text-dark-navy"
                aria-hidden="true"
              />
            </LinkBase>
          </div>
        </div>
        <div>
          <div className="mb-2xs grid	auto-rows-auto gap-sm lg:gap-2xs">
            {BELONG_PRO_LINKS.map((link) => {
              if (link.label === 'REFER FOR') {
                const { to } = link;
                const pathname = routerPathname;
                const href = to || { pathname };

                return (
                  <Link key={link.label} href={href}>
                    <Text
                      variant="body"
                      fontWeight="semibold"
                      className="lg:p1"
                    >
                      {link.label} <span className="text-green">$1,000</span>
                    </Text>
                  </Link>
                );
              }
              return (
                <FooterLink
                  key={link.label}
                  pathname={routerPathname}
                  {...link}
                />
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-2xs grid	auto-rows-auto gap-sm lg:gap-2xs">
            {WHO_WE_ARE_COLUMN_LINKS.map((link) => {
              return (
                <FooterLink
                  key={link.label}
                  pathname={routerPathname}
                  {...link}
                />
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-2xs grid	auto-rows-auto gap-sm lg:gap-2xs">
            {SUPPORT_COLUMN_LINKS.map((link) => {
              const linkProps = {
                ...link,
              };

              return (
                <FooterLink
                  key={link.label}
                  pathname={routerPathname}
                  {...linkProps}
                />
              );
            })}
            {/* The CCPA opt-out. It sits with the help links rather than the
                company ones: it is something a visitor does, not something
                they read about us. */}
            <ConsentPreferencesLink />
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row md:justify-between">
        <div className="mb-xl md:mb-0">
          <div className="mb-lg flex flex-col gap-[4px]">
            <Text variant="p1">
              Belong holds real estate, insurance, and financing{' '}
              <Link variant="text" href="/licenses">
                licenses
              </Link>
              , in various states.
            </Text>
            <Text variant="p1" className="font-semibold">
              <Link
                variant="text"
                href="https://belong-consumer-assets.s3.us-west-1.amazonaws.com/home-page/fairhousingnotice_10-4-23.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                New York Fair Housing Notice
              </Link>
            </Text>
            <Text variant="p1">
              <span className="font-normal">TREC:</span>{' '}
              <span className="font-semibold">
                <Link
                  variant="text"
                  href="https://belong-consumer-assets.s3.us-west-1.amazonaws.com/home-page/IABS+1-2.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Information about brokerage services
                </Link>
                ,{' '}
                <Link
                  variant="text"
                  href="https://www.trec.texas.gov/forms/consumer-protection-notice"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Consumer protection notice
                </Link>
              </span>
            </Text>
          </div>
        </div>
        <Text variant="p1" as="small">
          Copyright © {new Date().getFullYear()} Belong, Inc.
        </Text>
      </div>
    </footer>
  );
}
